import { Injectable } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';
import { HttpHeaders, HttpClient, HttpParams } from '@angular/common/http';
import { ObservableService } from './observable.service';
import { HttpStatus, StringConst } from '../constants/const';
import { LoggerManagerService } from './logger-manager.service';
import { SpinnerConfig } from '../model/local/spinner';
import { SpinnerNames } from '../constants/spinners';
import { ErrorService } from './error.service';

@Injectable({
  providedIn: 'root',
})
export class ConsumeService {
  private httpCounter = 0;

  constructor(
    private http: HttpClient,
    private observableService: ObservableService,
    private loggerService: LoggerManagerService,
    private errorService: ErrorService,
  ) {}

  httpGet<T>(
    url: string,
    params?: any,
    headers?: any,
    spinnerName = SpinnerNames.principal
  ): Observable<T> {
    this.httpCounter++;
    let objHeaders = new HttpHeaders();
    if (headers) {
      Object.keys(headers).forEach((key) => {
        objHeaders = objHeaders.append(key, headers[key]);
      });
    }

    let objParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        objParams = objParams.append(key, params[key]);
      });
    }

    const spinnerInfo = new SpinnerConfig();
    spinnerInfo.name = spinnerName;
    spinnerInfo.status = true;
    this.observableService.setIsLoading(spinnerInfo);
    return new Observable<T>((observer) => {
      this.http
        .get<any>(url, { headers: objHeaders, params: objParams })
        .subscribe(
          (response) => {
            this.returnResponse(observer, response, spinnerName);
          },
          (err) => {
            this.returnError(observer, err, spinnerName);
          }
        );
    });
  }

  httpPost<T>(
    url: string,
    body: any,
    headers?: any,
    hideLoading = false,
    spinnerName = SpinnerNames.principal,
    skipErrorDialog = false
  ): Observable<T> {
    this.httpCounter++;
    let objHeaders = new HttpHeaders();
    if (headers) {
      Object.keys(headers).forEach((key) => {
        objHeaders = objHeaders.append(key, headers[key]);
      });
    }
    if (!hideLoading) {
      const spinnerInfo = new SpinnerConfig();
      spinnerInfo.name = spinnerName;
      spinnerInfo.status = true;
      this.observableService.setIsLoading(spinnerInfo);
    }
    return new Observable<T>((observer) => {
      this.http.post<any>(url, body, { headers: objHeaders }).subscribe(
        (response) => {
          this.returnResponse(observer, response, spinnerName);
        },
        (err) => {
          this.returnError(observer, err, spinnerName, skipErrorDialog);
        }
      );
    });
  }

  httpDelete<T>(
    url: string,
    params?: any,
    headers?: any,
    spinnerName = SpinnerNames.principal
  ): Observable<T> {
    this.httpCounter++;
    let objHeaders = new HttpHeaders();
    if (headers) {
      Object.keys(headers).forEach((key) => {
        objHeaders = objHeaders.append(key, headers[key]);
      });
    }

    let objParams = new HttpParams();
    if (params) {
      Object.keys(params).forEach((key) => {
        objParams = objParams.append(key, params[key]);
      });
    }

    const spinnerInfo = new SpinnerConfig();
    spinnerInfo.name = spinnerName;
    spinnerInfo.status = true;
    this.observableService.setIsLoading(spinnerInfo);
    return new Observable<T>((observer) => {
      this.http
        .delete<any>(url, { headers: objHeaders, params: objParams })
        .subscribe(
          (response) => {
            this.returnResponse(observer, response, spinnerName);
          },
          (err) => {
            this.returnError(observer, err, spinnerName);
          }
        );
    });
  }

  httpPut<T>(
    url: string,
    body: any,
    headers?: any,
    spinnerName = SpinnerNames.principal
  ): Observable<T> {
    this.httpCounter++;
    let objHeaders = new HttpHeaders();
    if (headers) {
      Object.keys(headers).forEach((key) => {
        objHeaders = objHeaders.append(key, headers[key]);
      });
    }
    const spinnerInfo = new SpinnerConfig();
    spinnerInfo.name = spinnerName;
    spinnerInfo.status = true;
    this.observableService.setIsLoading(spinnerInfo);
    return new Observable<T>((observer) => {
      this.http.put<any>(url, body, { headers: objHeaders }).subscribe(
        (response) => {
          this.returnResponse(observer, response, spinnerName);
        },
        (err) => {
          this.returnError(observer, err, spinnerName);
        }
      );
    });
  }
  private returnResponse<T>(
    observer: Subscriber<T>,
    response: any,
    spinnerName: string
  ): void {
    if (response.code && response.code !== HttpStatus.ok) {
      this.loggerService.exception(
        response.userError ?? StringConst.empty,
        false
      );
    }
    this.handleLoading();
    observer.next(response);
    observer.complete();
  }

  private returnError<T>(
    observer: Subscriber<T>,
    error: any,
    spinnerName: string,
    skipErrorDialog = false
  ): void {
    this.loggerService.exception(error.userError, true);
    if (!skipErrorDialog) {
      this.errorService.http(error);
    }
    this.handleLoading();
    observer.error(error);
  }

  private handleLoading(): void {
    this.httpCounter--;
    if (this.httpCounter === 0) {
      const spinnerNames = Object.values(SpinnerNames);
      spinnerNames.forEach(spinner => {
        const spinnerInfo = new SpinnerConfig();
        spinnerInfo.status = false;
        spinnerInfo.name = spinner;
        this.observableService.setIsLoading(spinnerInfo);
      });
    }
  }
}
